import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, CheckCircle2, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const { sendMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);

  // If the user lands here with a session already, bounce them home.
  const from = location.state?.from?.pathname || '/';
  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, navigate, from]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    setError('');

    if (!EMAIL_RE.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    const res = await sendMagicLink(email.trim());
    inFlight.current = false;
    setSubmitting(false);

    if (res.success) {
      setSent(true);
    } else {
      setError(res.error || 'Failed to send magic link.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-5 border-b border-stone-900/80">
        <Link to="/login" className="flex items-center gap-2 w-fit">
          <span className="text-alfred-gold font-serif text-2xl tracking-wide">
            Alfred
          </span>
          <span className="text-xs uppercase tracking-[0.25em] text-stone-500">
            Voice
          </span>
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="card">
            {sent ? (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-900/40 border border-emerald-800 mb-4">
                  <CheckCircle2 className="text-emerald-300" size={22} />
                </div>
                <h1 className="text-2xl text-stone-100 mb-2">Check your email</h1>
                <p className="text-sm text-stone-400">
                  We sent a magic link to{' '}
                  <span className="text-stone-200">{email}</span>. Click it to
                  sign in. The link expires in 10 minutes.
                </p>
                <button
                  type="button"
                  className="btn-ghost mt-6"
                  onClick={() => {
                    setSent(false);
                    setEmail('');
                  }}
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl text-stone-100 mb-1">Sign in</h1>
                <p className="text-sm text-stone-400 mb-6">
                  Enter your email and we'll send you a magic link.
                </p>

                <form onSubmit={onSubmit} noValidate>
                  <label htmlFor="email" className="field-label">
                    Email
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none"
                    />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="field-input pl-9"
                      disabled={submitting}
                    />
                  </div>

                  {error && (
                    <div className="mt-4">
                      <ErrorBanner message={error} />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !email}
                    className="btn-primary w-full mt-6"
                  >
                    {submitting ? 'Sending…' : 'Send magic link'}
                    {!submitting && <ArrowRight size={16} />}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-stone-500">
            Having trouble signing in? Contact your account admin.
          </p>
        </div>
      </div>
    </div>
  );
}
